import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import { add, canvasOf, fade, fill, place, playerColor, sprite } from '../fx/common';
import {
  bankFrame, formulaRow, materialCards, monsterPlate, sideRow,
} from '../fx/materialBank';

/**
 * **シンクロ召喚の変身バンク**（Issue #598 v3。指示書 `../direction/material_summon_synchro.md`。キービジュアルは無い＝方向は人が決めた）。
 * 疑似イベント `material_summon`（`editorPorts/materialSummon.js`）の状況 `material_summon_synchro`。**motion なし**（盤は触らない。
 * 素材はこの前の move でもう墓地、出るモンスターは後の `spsummoning` で盤に出る＝召喚バンクと 2 回あってよい）。
 *
 * ```
 * 0      溜め: 舞台を暗く（層の暗幕。HUD は落とさない）。召喚した人の側から素材のカード（表）が飛んできて横一列
 *        （画面の中央を輪のために空け、チューナーは左・ほかは右）。カードの下に ★3（銀の金属文字）
 * 380    数式 ★3 + ★4 = ★7 を左から叩き込む（項は 380〜600、「= 合計」は 820。最後の ★7 だけ大きく金）。
 *        **カードは見えたまま**で、項を叩くたびに対応するカードが揺れて硬い縁が 2 フレーム白く光る（合計では全部）（v2。ユーザー 2026-10-01）
 * 1000   **全部の素材が同時に変身**: カードの形の閃光 2 フレーム・ヒットストップ 100ms・揺れ。チューナーは緑の硬い輪
 *        （チューナーの所にカードの大きさで出て、1100〜1300 で画面の中央へ飛びながら奥へ連なるトンネルに開く。輪の数 = チューナーのレベル）、
 *        ほかは白い星（レベルの数）と鋼の破片
 * 1250   星が順に弧を描いて輪のトンネルへ飛び込み、奥へ駆け抜ける（〜1700）。くぐるたびにその輪が 1 フレーム白く光る
 * 1700   輪のトンネルを白い硬い線の柱（縦の線の束）が貫く → 1760 閃光 2 フレーム・ヒットストップ 100ms・揺れ。
 *        輪は弾けて広がり消え、数式は柱へ吸われて消える
 * 2100   柱の中から出るモンスター: 鋼の板とイラストが縦の細い隙間から開き、影（黒）→ 色。2300 名前、2380 SYNCHRO SUMMON の帯
 *        （出るモンスターがまだ入力されていなければ柱が抜けて終わる。尺も 2400 に縮む）
 * 2800   全部が縦に細く潰れて消え（最後に横の硬い線 1 フレーム）、暗幕を戻す。何も残さない
 * ```
 *
 * トンネル・柱・出るモンスターは**画面の中央に固定**（v3。ユーザー 2026-10-01）。素材のカードは中央の輪の左右の外側に並べ（チューナーが左・ほかが右）、
 * 変身でチューナーの所に出た輪が中央へ飛んで奥へ開く（輪がチューナーから中央へつながって見える）。
 *
 * **分からないときも成立させる**（指示書「イベントの中身」）: チューナーが 1 枚も分からなければ 1 枚目をチューナー扱いにして輪にする。
 * レベルが分からない素材は輪 3 / 星 2 の既定で描き、★ の数字と数式は出さない（数式は全部のレベルが分かったときだけ）。
 *
 * 輪と星は硬い形（`kit.hardRingTexture` / `kit.hardStarTexture`。にじみ無し。Issue #598 で kit に足した）。
 */
export const D = 3000;
export const D_NO_RESULT = 2400;
const EQ = [380, 600, 820];  // 数式: 最初の項 / 最後の項 / 「= 合計」
const MORPH = 1000;          // 全部の素材が同時に変身
const MORPH_STOP = 100;      // その止め
const RING_OPEN = [1100, 1300];   // 輪がチューナーの所から画面の中央へ飛びながら奥へ連なるトンネルに開く
const STAR_POP = 80;         // 星が弾けて出揃うまで
const RUN = [1250, 1700];    // 星が輪をくぐる区間（最後の星がくぐり終わる）
const RUN_DUR = 320;         // 星 1 つがトンネルを抜けるまで
const PILLAR = 1700;
const HIT = 1760;
const STOP = 100;
const PILLAR_NARROW = [1950, 2150];
const MON = 2100;
const MON_OPEN = 120;
const MON_COLOR = [2160, 2300];
const NAME_HIT = 2300;
const BAND_HIT = 2380;

const SYNCHRO_GREEN = '#3cf07a';
const RING_R = 250;          // 一番手前の輪の半径
const RING_DEPTH = 0.74;     // 奥の輪ほど小さく
const RING_STEP = [26, -30]; // 奥の輪ほど中心が右上へ（消失点）
const MAX_RINGS = 8;
const MAX_STARS = 12;
const STAR_PX = 92;
const UNKNOWN_RING = 3;
const UNKNOWN_STAR = 2;
const SHARDS_PER_CARD = 10;
const PILLAR_BARS = 26;

/** ヒットストップの時間の写し（柱が貫いた後は STOP だけ遅れる） */
const hs = (t) => (t < HIT ? t : (t < HIT + STOP ? HIT : t - STOP));
/** 変身の止めの時間の写し（変身の後の破片・輪の回りは止めの間だけ止まる） */
const ms = (t) => (t < MORPH + MORPH_STOP ? Math.min(t, MORPH) : t - MORPH_STOP);
const lvOf = (m) => (Number.isFinite(m?.level) && m.level > 0 ? m.level : null);

/**
 * **バンクの段取り**（純関数。テストが見る）: 誰が輪で誰が星か・輪と星の数・数式の項。
 * チューナーが分からなければ 1 枚目をチューナー扱い。レベルが分からなければ既定の数で描き、数式は出さない。
 */
export function bankPlan(payload) {
  const materials = Array.isArray(payload?.materials) ? payload.materials : [];
  const known = materials.filter((m) => m?.isTuner === true);
  const tuners = known.length ? known : materials.slice(0, 1);
  const nonTuners = materials.filter((m) => !tuners.includes(m));
  const rings = Math.max(1, Math.min(MAX_RINGS, tuners.reduce((a, m) => a + (lvOf(m) ?? UNKNOWN_RING), 0)));
  const starsPer = nonTuners.map((m) => lvOf(m) ?? UNKNOWN_STAR);
  let left = MAX_STARS;
  const starsOf = starsPer.map((n) => { const k = Math.max(0, Math.min(n, left)); left -= k; return k; });
  const order = [...tuners, ...nonTuners];
  const levels = order.map(lvOf);
  const allKnown = order.length > 0 && levels.every((l) => l != null);
  const total = allKnown ? levels.reduce((a, b) => a + b, 0) : null;
  const formula = allKnown ? { terms: levels, total } : null;
  return { tuners, nonTuners, order, rings, starsOf, stars: starsOf.reduce((a, b) => a + b, 0), formula };
}

/** 帯の尺（出るモンスターが無ければ柱が抜けて終わる） */
export const durationOf = (payload) => (payload?.result?.code != null ? D : D_NO_RESULT);

/** 数式の項を叩き込む時刻（最初の項は 380、残りの項は 600 までに等分。「= 合計」は `EQ[2]`） */
export function formulaTimes(n) {
  if (n <= 1) return [EQ[0]];
  return Array.from({ length: n }, (_, i) => EQ[0] + ((EQ[1] - EQ[0]) * i) / (n - 1));
}
export const SUM_AT = EQ[2];
export const MORPH_AT = MORPH;

/**
 * **素材のカードの横の並び**（純関数）: 画面の中央を輪（半径 `RING_R`）のために空け、チューナーは中央の輪の左の外側から左へ、
 * 非チューナーは右の外側から右へ並べる（v3。輪・柱は画面の中央に固定。ユーザー 2026-10-01）。はみ出すなら側ごとに間を詰める。
 * 返すのは各カードの x（`order` ＝ チューナー → 非チューナー の順）とトンネルの中心 x（常に画面の中央）
 */
export const rowLayout = (nT, nN, cw, W = 1920) => sideRow(nT, nN, cw, { W, hole: RING_R });

/** 星 k がトンネルを走り始める時刻（最後の星が RUN の終わりに抜け終わる） */
function starStart(k, n) {
  const span = RUN[1] - RUN[0] - RUN_DUR;
  const gap = n > 1 ? Math.min(70, span / (n - 1)) : 0;
  return RUN[0] + k * gap;
}
/** 星が輪 j をくぐる（トンネルの進み）。0〜0.4 で入口へ、0.4〜1 で奥へ */
const APPROACH = 0.4;
const crossP = (j, rings) => APPROACH + (1 - APPROACH) * (j / (rings + 0.3));

function sceneFor(payload) {
  const plan = bankPlan(payload);
  const out = [
    'reset',
    {
      ms: 0,
      grade: {
        contrast: {
          keys: [
            { ms: MORPH - 1, value: 1 }, { ms: MORPH, value: 1.35 }, { ms: MORPH + MORPH_STOP, value: 1.35, ease: 'hold' }, { ms: MORPH + MORPH_STOP + 1, value: 1 },
            { ms: HIT - 1, value: 1 }, { ms: HIT, value: 1.4 }, { ms: HIT + STOP, value: 1.4, ease: 'hold' }, { ms: HIT + STOP + 1, value: 1 },
          ],
        },
        chroma: {
          keys: [
            { ms: MORPH - 1, value: 0 }, { ms: MORPH, value: 0.012 }, { ms: MORPH + 34, value: 0, ease: 'ease_out' },
            { ms: HIT - 1, value: 0 }, { ms: HIT, value: 0.014 }, { ms: HIT + 34, value: 0, ease: 'ease_out' },
          ],
        },
      },
    },
    { ms: MORPH + MORPH_STOP, shake: { amp: 26, freq: 24, decayMs: 320 } },
    { ms: HIT + STOP, shake: { amp: 34, freq: 22, decayMs: 420 } },
  ];
  if (plan.formula) {
    const slams = [...new Set(formulaTimes(plan.formula.terms.length).map(Math.round)), EQ[2]];
    slams.forEach((t, i) => out.push({ ms: t + 10, shake: { amp: i === slams.length - 1 ? 16 : 9, freq: 28, decayMs: 160 } }));
  }
  if (payload?.result?.code != null) {
    out.push({ ms: NAME_HIT, shake: { amp: 8, freq: 30, decayMs: 160 } });
    out.push({ ms: BAND_HIT, shake: { amp: 14, freq: 26, decayMs: 220 } });
  }
  return out;
}

export default defineEffect({
  situation: 'material_summon_synchro',
  role: 'effect',
  label: 'シンクロ召喚の変身バンク（素材と数式 → 同時に輪と星 → 光の柱 → 出るモンスター）',
  color: SYNCHRO_GREEN,
  duration: ({ payload } = {}) => durationOf(payload),
  scene: (ctx) => sceneFor(ctx?.payload),
  sounds: ({ payload } = {}) => {
    const plan = bankPlan(payload);
    const out = [{ at: 0, name: 'phase_change', gain: 0.55 }];
    if (plan.formula) {
      formulaTimes(plan.formula.terms.length).forEach((t) => out.push({ at: Math.round(t) - 10, name: 'set_card', gain: 0.7 }));
      out.push({ at: EQ[2] - 10, name: 'set_card', gain: 0.9 });
    }
    out.push({ at: MORPH - 10, name: 'effect', gain: 0.85 });
    for (let k = 0; k < plan.stars; k += 1) {
      const t = starStart(k, plan.stars) + RUN_DUR * crossP(0, plan.rings);
      out.push({ at: Math.round(t), name: 'draw', gain: 0.35 });
    }
    out.push({ at: HIT - 15, name: 'attack', gain: 0.95 });
    if (payload?.result?.code != null) out.push({ at: MON, name: 'summon' });
    return out;
  },

  layers: [{
    id: 'bank',
    label: '素材と数式 → 同時に輪と星 → 光の柱 → 出るモンスター',
    space: 'screen',
    script: (ctx) => {
      const { THREE, kit } = ctx;
      const { w: W, h: H } = canvasOf(ctx);
      const { payload } = ctx;
      const p = payload.player === 1 ? 1 : 0;
      const pc = playerColor(ctx, p);
      const plan = bankPlan(payload);
      const result = payload.result?.code != null ? payload.result : null;
      const dur = durationOf(payload);
      const rand = seeded(`${ctx.seed}:synchro`);
      const frame = bankFrame(ctx, { dur });
      const { layer, metal } = frame;

      // --- 素材のカード（中央の輪の左右の外側。チューナーが左・ほかが右。カードの下に ★）
      const n = plan.order.length;
      const CW = Math.min(230, 1400 / Math.max(1, n) - 60);
      const ROW_Y = 600;
      const CY = ROW_Y;           // トンネルの入口の中心（画面の中央・カードの行の高さ）
      const lay = rowLayout(plan.tuners.length, plan.nonTuners.length, CW, W);
      const TX = lay.tx;
      const sumTimes = plan.formula ? formulaTimes(plan.formula.terms.length) : [];
      // 数式の項を叩いた時刻（このカードの数）と「= 合計」で揺れ・縁が光る
      const row = materialCards(ctx, frame, {
        materials: plan.order, player: p, xs: lay.xs, y: ROW_Y, cw: CW, rand, hitsOf: (i) => (plan.formula ? [sumTimes[i], EQ[2]] : []),
      });
      const CH = row.ch;
      const mats = row.items.map((mc) => {
        const lv = lvOf(mc.m);
        const label = lv != null ? metal(`★${lv}`, 60) : null;
        if (label) add(layer(6), label.m, 6);
        return Object.assign(mc, { label, isTuner: plan.tuners.includes(mc.m) });
      });

      // --- 輪のトンネル（変身でチューナーの所に現れ、画面の中央へ飛びながら奥へ連なって開く）
      const ringTex = kit.hardRingTexture(SYNCHRO_GREEN, { size: 512, band: 0.08, notches: 12 });
      const ringFlashTex = kit.hardRingTexture('#ffffff', { size: 512, band: 0.08, notches: 12 });
      const tunerSrc = mats.filter((x) => x.isTuner);
      const ringAt = (z) => ({ x: TX + RING_STEP[0] * z, y: CY + RING_STEP[1] * z, s: RING_DEPTH ** z });
      const ring0 = Math.min(1, (CH * 0.55) / RING_R);   // 変身の瞬間の大きさ（カードの大きさ）
      const rings = Array.from({ length: plan.rings }, (_, j) => {
        const m = add(layer(3), sprite(ctx, ringTex, RING_R * 2, RING_R * 2), 3);
        const f = add(layer(3), sprite(ctx, ringFlashTex, RING_R * 2, RING_R * 2), 3);
        const src = tunerSrc[j % Math.max(1, tunerSrc.length)] ?? { x: TX };
        return { m, f, j, src, spin: (j % 2 === 0 ? 1 : -1) * (0.5 + 0.15 * j) };
      });

      // 輪が中央へ飛ぶ間の硬い線（チューナーの所 → 輪。チューナーごとに 1 本）
      const ringTrails = tunerSrc.map((src) => ({ src, m: add(layer(2.5), fill(ctx, SYNCHRO_GREEN, 1, 1), 2.5), w: add(layer(2.6), fill(ctx, '#ffffff', 1, 1), 2.6) }));

      // --- 星（変身で非チューナーのカードの所に。レベルの数だけ）と、その後ろの硬い線
      const starTex = kit.hardStarTexture('#ffffff', { size: 128 });
      const stars = [];
      mats.filter((x) => !x.isTuner).forEach((mc, ci) => {
        const cnt = plan.starsOf[ci] ?? 0;
        for (let k = 0; k < cnt; k += 1) {
          const idx = stars.length;
          const m = add(layer(8), sprite(ctx, starTex, STAR_PX, STAR_PX), 8);
          const trail = add(layer(7), fill(ctx, '#ffffff', 1, 1), 7);
          stars.push({
            m, trail, sx: mc.x + (rand() - 0.5) * CW * 0.5, sy: ROW_Y - CH * 0.35 + ((k + 0.5) / cnt) * CH * 0.7,
            start: starStart(idx, plan.stars), spin: (rand() - 0.5) * 8,
          });
        }
      });
      // 星が輪 j をくぐる時刻の一覧（輪の 1 フレームの光）
      const crossings = plan.rings ? stars.map((s) => rings.map((r) => s.start + RUN_DUR * crossP(r.j, plan.rings))) : [];

      // --- 変身の鋼の破片（非チューナーのカードから）
      const shards = [];
      mats.filter((x) => !x.isTuner).forEach((mc) => {
        for (let k = 0; k < SHARDS_PER_CARD; k += 1) {
          const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), kit.plain(null, 1));
          m.material.color?.set?.(k % 3 === 0 ? '#e6e9ee' : '#9aa1ad');
          add(layer(8), m, 8);
          shards.push({
            m, x: mc.x + (rand() - 0.5) * CW, y: ROW_Y + (rand() - 0.5) * CH, a: rand() * Math.PI * 2,
            size: 10 + rand() * 18, stretch: 1.5 + rand() * 1.5, speed: 400 + rand() * 800, spin: (rand() - 0.5) * 26,
          });
        }
      });

      // --- 数式（★3 + ★4 = ★7。最後だけ大きく金）
      // 紋章の下のフェイズ名（y ≒ 160）に掛からず、カードの上端（y ≒ 430）より上
      const FY = 290;
      const eqTokens = [];
      if (plan.formula) {
        plan.formula.terms.forEach((lv, i) => {
          if (i > 0) eqTokens.push({ text: '+', size: 96, at: sumTimes[i] });
          eqTokens.push({ text: `★${lv}`, size: 104, at: sumTimes[i] });
        });
        eqTokens.push({ text: '=', size: 96, at: EQ[2] });
        eqTokens.push({ text: `★${plan.formula.total}`, size: 150, at: EQ[2], gold: true });
      }
      const formula = formulaRow(ctx, frame, eqTokens);

      // --- 光の柱（縦の硬い線の束。トンネルの中心から上下へ）
      const pcols = ['#ffffff', '#ffffff', '#d8ffe6', SYNCHRO_GREEN];
      const bars = Array.from({ length: PILLAR_BARS }, (_, i) => {
        const m = add(layer(11), fill(ctx, pcols[i % pcols.length], 1, 1), 11);
        const r = rand() * 2 - 1;
        return { m, dx: r * Math.abs(r) * 170, w: 4 + rand() * 30, seed: rand() * 100 };
      });
      const flash = add(layer(14), place(fill(ctx, '#ffffff', W, H), W / 2, H / 2), 14);

      // --- 出るモンスター（鋼の板・イラスト・影・名前・SYNCHRO の帯）。柱（トンネル）の所＝画面の中央
      const mon = result ? monsterPlate(ctx, frame, {
        result, player: p, pc, accent: '#1f8a4a', bandColor: SYNCHRO_GREEN, tag: 'SYNCHRO SUMMON', cx: TX,
      }) : null;

      return {
        root: frame.top,
        update: ({ localMs: t }) => {
          const u = hs(t);
          const v = ms(t);
          frame.update(t);

          // --- 素材のカード: 召喚した人の側から飛んできて並ぶ → 数式の間は見えたまま（項ごとに揺れ・縁が光る）→ 1000 で同時に変身
          mats.forEach((mc) => {
            const { x, y, s: bump, rot, lit } = row.pose(mc, t);
            place(mc.c.mesh, x, y, { s: bump, rot });
            fade(mc.c.mesh, t < MORPH ? 1 : 0);
            // 硬い縁（項を叩いた頭の 2 フレーム）
            row.edges(mc, x, y, bump, lit);
            // 変身の閃光（カードの形。2 フレーム）
            place(mc.flash, mc.x, ROW_Y);
            fade(mc.flash, t >= MORPH && t < MORPH + 34 ? 0.95 : 0);
            if (mc.label) {
              place(mc.label.m, x, y + CH / 2 + 48);
              fade(mc.label.m, seg(t, 150, 250) * (t < MORPH ? 1 : 0));
            }
          });

          // --- 輪: 変身でチューナーの所に現れる（止めの間はその大きさ）→ 中央へ飛びながら奥へ連なって開く（後ろに緑の硬い線）→ 回る →
          //     星がくぐると 1 フレーム白 → 柱で弾けて広がり消える
          const burst = seg(u, HIT, HIT + 140);
          const openK = tween(t, RING_OPEN[0], RING_OPEN[1], 'expo_out');
          rings.forEach((r, ri) => {
            const at = ringAt(r.j);
            const x = r.src.x + (at.x - r.src.x) * openK;
            const y = ROW_Y + (at.y - ROW_Y) * openK;
            const s = (ring0 + (at.s - ring0) * openK) * (1 + 1.4 * burst);
            const rot = r.spin * (Math.max(0, v - MORPH) / 1000);
            place(r.m, x, y, { s, rot });
            place(r.f, x, y, { s, rot });
            const shown = t >= MORPH && burst < 1;
            const flashOn = crossings.some((cs) => t >= cs[ri] && t < cs[ri] + 34) || (t >= HIT && t < HIT + 34) || (t >= MORPH && t < MORPH + 34);
            fade(r.m, shown && !flashOn ? (1 - burst) * (1 - burst) : 0);
            fade(r.f, shown && flashOn ? 1 : 0);
          });

          // 輪が中央へ飛ぶ間の硬い線（緑の太い線に白の芯。飛び終わる頃に細くなって消える）
          ringTrails.forEach(({ src, m, w }) => {
            const hx = src.x + (TX - src.x) * openK; const hy = ROW_Y + (CY - ROW_Y) * openK;
            const len = Math.hypot(hx - src.x, hy - ROW_Y);
            const thin = 1 - seg(t, RING_OPEN[0] + 60, RING_OPEN[0] + 180);
            const on = t >= RING_OPEN[0] && thin > 0 && len > 4;
            const a = Math.atan2(hy - ROW_Y, hx - src.x);
            place(m, (src.x + hx) / 2, (ROW_Y + hy) / 2, { sx: Math.max(0.001, len), sy: Math.max(0.001, 18 * thin), rot: a });
            place(w, (src.x + hx) / 2, (ROW_Y + hy) / 2, { sx: Math.max(0.001, len), sy: Math.max(0.001, 5 * thin), rot: a });
            fade(m, on ? 1 : 0);
            fade(w, on ? 1 : 0);
          });

          // --- 変身の鋼の破片（止めの間は止まる）
          const since = Math.max(0, (v - MORPH) / 1000);
          shards.forEach((s) => {
            const k = Math.min(1, since / 0.6);
            const r = s.speed * since;
            s.m.position.set(s.x + Math.cos(s.a) * r, s.y + Math.sin(s.a) * r + 1400 * since * since, 0);
            s.m.rotation.z = s.a + s.spin * since;
            s.m.scale.set(s.size * s.stretch, s.size, 1);
            fade(s.m, t >= MORPH && k < 1 ? 1 - k * k : 0);
          });

          // --- 星: 変身でカードの所に弾けて出る → 順に入口へ弧を描いて飛び込み、輪をくぐって奥へ小さくなって消える
          stars.forEach((s) => {
            const pop = t < MORPH + MORPH_STOP ? 1.25 : 1.25 - 0.25 * tween(t, MORPH + MORPH_STOP, MORPH + MORPH_STOP + STAR_POP, 'back_out');
            const pr = seg(t, s.start, s.start + RUN_DUR);
            const posAt = (q) => {
              if (q <= APPROACH) {
                const a = tween(q, 0, APPROACH, 'ease_in');
                const ctrlX = (s.sx + TX) / 2; const ctrlY = Math.min(s.sy, CY) - 260;
                const bx = (1 - a) * (1 - a) * s.sx + 2 * (1 - a) * a * ctrlX + a * a * TX;
                const by = (1 - a) * (1 - a) * s.sy + 2 * (1 - a) * a * ctrlY + a * a * CY;
                return { x: bx, y: by, s: 1 - 0.1 * a };
              }
              const z = ((q - APPROACH) / (1 - APPROACH)) * (plan.rings + 0.3);
              const at = ringAt(z);
              return { x: at.x, y: at.y, s: 0.9 * at.s };
            };
            const cur = posAt(pr);
            place(s.m, cur.x, cur.y, { s: cur.s * pop, rot: s.spin * (Math.max(0, v - MORPH) / 1000) });
            fade(s.m, t >= MORPH && pr < 1 ? 1 : 0);
            // 後ろの硬い線（走っている間だけ）
            const back = posAt(Math.max(0, pr - 0.12));
            const dx = cur.x - back.x; const dy = cur.y - back.y;
            const len = Math.hypot(dx, dy);
            place(s.trail, (cur.x + back.x) / 2, (cur.y + back.y) / 2, { sx: Math.max(0.001, len), sy: 5 * cur.s, rot: Math.atan2(dy, dx) });
            fade(s.trail, pr > 0 && pr < 1 && len > 4 ? 0.85 : 0);
          });

          // --- 数式: 左から叩き込む（2.2 倍 → 1 倍）→ 柱が来たら柱へ吸われて消える
          formula.update(t, { y: FY, toX: TX, suck: tween(t, PILLAR, HIT, 'power2_in'), hideAt: HIT });

          // --- 光の柱: 貫く（1700〜1760）→ 止め → 揺らぎ → 細くなって抜ける
          const shoot = tween(t, PILLAR, HIT, 'expo_out');
          const narrow = 1 - tween(t, PILLAR_NARROW[0], PILLAR_NARROW[1], 'power2_in');
          const step = Math.floor(u / 33);
          bars.forEach((b) => {
            const jit = 0.65 + 0.35 * Math.abs(Math.sin(b.seed + step * 1.7));
            const hgt = H * 1.6 * shoot;
            place(b.m, TX + b.dx * narrow, CY - hgt * 0.25, { sx: Math.max(0.001, b.w * jit * narrow), sy: Math.max(0.001, hgt) });
            fade(b.m, t >= PILLAR && narrow > 0.01 ? 0.95 : 0);
          });
          fade(flash, t >= HIT && t < HIT + 34 ? 0.6 : 0);

          // --- 出るモンスター
          mon?.update(t, {
            at: MON, open: MON_OPEN, colorAt: MON_COLOR, nameAt: NAME_HIT, bandAt: BAND_HIT,
          });
        },
      };
    },
  }],
}, 'material_summon_synchro_effect.js');
